import type { CatalogList, CategoryList, ProductList, ProductSummary } from '@arm-pd/domain';

const API_BASE = `${import.meta.env.VITE_BFF_URL || ''}/api/v1`;

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: 'An unknown error occurred' },
    }));
    throw new Error(error.error?.message || 'Request failed');
  }

  const result: APIResponse<T> = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Request failed');
  }

  if (result.data === undefined) {
    throw new Error('Response data is undefined');
  }

  return result.data;
}

/**
 * Fetch all catalogs
 */
export async function fetchCatalogs(): Promise<CatalogList> {
  const response = await fetch(`${API_BASE}/catalogs`, { credentials: 'include' });
  return handleResponse<CatalogList>(response);
}

/**
 * Fetch categories for a catalog
 */
export async function fetchCategories(catalogId: string): Promise<CategoryList> {
  const response = await fetch(`${API_BASE}/catalogs/${catalogId}/categories`, {
    credentials: 'include',
  });
  return handleResponse<CategoryList>(response);
}

/**
 * Fetch products with optional filters
 */
export interface ProductFilterParams {
  catalogId?: string;
  categoryId?: string;
  search?: string;
  currency?: string;
  limit?: number;
  cursor?: string;
  offset?: number;
}

export async function fetchProducts(params: ProductFilterParams = {}): Promise<ProductList> {
  const queryParams = new URLSearchParams();

  if (params.catalogId) queryParams.set('catalogId', params.catalogId);
  if (params.categoryId) queryParams.set('categoryId', params.categoryId);
  if (params.search) queryParams.set('search', params.search);
  if (params.currency) queryParams.set('currency', params.currency);
  if (params.limit) queryParams.set('limit', params.limit.toString());
  if (params.cursor) queryParams.set('cursor', params.cursor);
  if (params.offset !== undefined) queryParams.set('offset', params.offset.toString());

  const queryString = queryParams.toString();
  const url = `${API_BASE}/products${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, { credentials: 'include' });
  return handleResponse<ProductList>(response);
}

/**
 * Fetch product details
 */
export async function fetchProductDetails(productId: string): Promise<ProductSummary> {
  const response = await fetch(`${API_BASE}/products/${productId}`, { credentials: 'include' });
  return handleResponse<ProductSummary>(response);
}

/**
 * Check API health
 */
export async function checkHealth(): Promise<{
  status: string;
  timestamp: string;
  checks: { salesforce: string };
}> {
  const response = await fetch(`${API_BASE}/health`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('Health check failed');
  }
  return response.json();
}
